import { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'

import { env } from '@percent/partner-dashboard/env'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { getPostHogApiHost } from '@percent/utility'

// Whether PostHog has loaded feature flags for the *current* identity. PostHog
// first evaluates flags for the anonymous visitor, then re-evaluates them after
// `identify`, so a flag can briefly report a stale (anonymous) value before
// settling. Consumers gate flag-dependent UI on this to avoid acting on that
// intermediate value. Defaults to true so consumers rendered outside the
// provider (e.g. tests, or when PostHog is disabled) behave as "ready".
const FeatureFlagsReadyContext = createContext(true)

// Fallback grace period before we treat flags as ready regardless of PostHog.
// `onFeatureFlags` may never fire if the PostHog endpoint is unreachable (most
// commonly blocked by an ad/privacy blocker), which would otherwise leave
// flag-gated UI waiting forever. After this we degrade to the default value.
const FEATURE_FLAGS_READY_TIMEOUT_MS = 3000

export const useAreFeatureFlagsReady = () => useContext(FeatureFlagsReadyContext)

export { FeatureFlagsReadyContext }

export function PostHogProvider({ children }: PropsWithChildren) {
  const apiKey = env.NX_PUBLIC_POSTHOG_API_KEY
  const initialized = useRef(false)
  const [areFeatureFlagsReady, setAreFeatureFlagsReady] = useState(false)

  const { user, partner } = useAuthState()

  useEffect(() => {
    if (!apiKey) return

    if (!initialized.current) {
      posthog.init(apiKey, {
        api_host: getPostHogApiHost(window.location.hostname),
        ui_host: 'https://eu.posthog.com',
        capture_pageview: false,
        capture_pageleave: false,
        autocapture: false,
        disable_session_recording: true,
        person_profiles: 'identified_only'
      })
      posthog.register({ app: 'partner-dashboard' })
      initialized.current = true
    }

    // Identity is (re)set below, which makes PostHog reload flags for it. Treat
    // flags as not-yet-ready until that reload completes so consumers don't act
    // on the previous identity's (e.g. anonymous) flag values.
    setAreFeatureFlagsReady(false)

    if (user?.id && partner?.id) {
      posthog.identify(user.id, { partnerId: partner.id })
    } else {
      posthog.reset()
    }
  }, [apiKey, user?.id, partner?.id])

  useEffect(() => {
    if (!apiKey) return undefined

    // Fires every time flags are (re)loaded — including after `identify` — at
    // which point the values reflect the current identity.
    return posthog.onFeatureFlags(() => setAreFeatureFlagsReady(true))
  }, [apiKey])

  useEffect(() => {
    if (!apiKey) return undefined

    // Safety net for when `onFeatureFlags` never fires (e.g. PostHog blocked by
    // an ad/privacy blocker). Re-armed on each identity change so it tracks the
    // same reload window as the reset above; cleared once flags settle.
    if (areFeatureFlagsReady) return undefined

    const timeoutId = setTimeout(() => setAreFeatureFlagsReady(true), FEATURE_FLAGS_READY_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [apiKey, areFeatureFlagsReady, user?.id, partner?.id])

  if (!apiKey) return <>{children}</>

  return (
    <FeatureFlagsReadyContext.Provider value={areFeatureFlagsReady}>
      <PHProvider client={posthog}>{children}</PHProvider>
    </FeatureFlagsReadyContext.Provider>
  )
}
